import React, { useState } from "react";
import { Carousel, Tag, Button, Tooltip } from "antd";
import { CloseOutlined, NotificationOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import { useApi } from "Hooks/ServicesContext";
import { getFullImageUrl } from "Helpers/functions";
import "./AnnouncementBar.scss";

// Post-login system announcements, shown at the top of the panel.
//   - banner slides (images)  -> auto-playing carousel
//   - text announcements      -> one moving (marquee) line
// Data: GET /announcements/active (already filtered to the user's depot and
// the live time window). Re-checked every 5 minutes so a newly published
// "maintenance at 10 PM" reaches users without a reload.
const TYPE_LABEL = { info: "Info", maintenance: "Maintenance", update: "Update", alert: "Alert" };
const TYPE_COLOR = { info: "blue", maintenance: "orange", update: "green", alert: "red" };

const openLink = (url) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

export default function AnnouncementBar() {
  const apiService = useApi();
  const [tickerHidden, setTickerHidden] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);

  const { data } = useQuery(["active-announcements"], () => apiService.getActiveAnnouncements(), {
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    retry: 0,
    onError: () => {},
  });

  const items = Array.isArray(data?.data) ? data.data : [];
  const banners = items.filter((a) => a.display_type === "banner" && a.image_url);
  const tickers = items.filter((a) => a.display_type !== "banner" && (a.message || a.title));

  if (!banners.length && !tickers.length) return null;

  // Slow the marquee down for long text so it stays readable (~60 px/s).
  const tickerChars = tickers.reduce((n, a) => n + (a.title || "").length + (a.message || "").length, 0);
  const duration = Math.max(20, Math.round(tickerChars * 0.25));

  const tickerContent = tickers.map((a) => (
    <span
      key={a.announcement_id}
      className={`announcement-ticker__item ${a.link_url ? "is-link" : ""}`}
      onClick={() => openLink(a.link_url)}
    >
      <Tag color={TYPE_COLOR[a.announcement_type] || "blue"} bordered={false}>
        {TYPE_LABEL[a.announcement_type] || "Info"}
      </Tag>
      <strong>{a.title}</strong>
      {a.message ? <> &mdash; {a.message}</> : null}
    </span>
  ));

  return (
    <div className="announcement-bar">
      {banners.length > 0 && !bannerHidden && (
        <div className="announcement-banners">
          <Carousel autoplay={banners.length > 1} autoplaySpeed={6000} dots={banners.length > 1} draggable>
            {banners.map((b) => (
              <div key={b.announcement_id}>
                <img
                  src={getFullImageUrl(b.image_url)}
                  alt={b.title}
                  title={b.message || b.title}
                  className={`announcement-banners__img ${b.link_url ? "is-link" : ""}`}
                  onClick={() => openLink(b.link_url)}
                />
              </div>
            ))}
          </Carousel>
          <Tooltip title="Hide banners">
            <Button
              size="small"
              shape="circle"
              icon={<CloseOutlined />}
              className="announcement-bar__close announcement-bar__close--banner"
              onClick={() => setBannerHidden(true)}
            />
          </Tooltip>
        </div>
      )}

      {tickers.length > 0 && !tickerHidden && (
        <div className="announcement-ticker" role="status" aria-live="polite">
          <span className="announcement-ticker__icon">
            <NotificationOutlined />
          </span>
          <div className="announcement-ticker__viewport">
            <div className="announcement-ticker__track" style={{ animationDuration: `${duration}s` }}>
              <span className="announcement-ticker__group">{tickerContent}</span>
              <span className="announcement-ticker__group" aria-hidden="true">
                {tickerContent}
              </span>
            </div>
          </div>
          <Tooltip title="Hide">
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              className="announcement-bar__close"
              onClick={() => setTickerHidden(true)}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
